import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MdArrowBackIosNew, MdHowToVote, MdOutlineDeleteOutline, MdOutlineVisibilityOff } from 'react-icons/md'
import { RiBarChartHorizontalFill } from 'react-icons/ri'
import { FaUsersRectangle } from 'react-icons/fa6'
import { FaRegCheckCircle } from 'react-icons/fa'
import { FiSettings } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { deletePoll, resetPollDeleteStatus, updateViewSettings, viewResponses } from 'features/polls/pollSlice'
import { setConfirmModalData, setViewConfirmModal } from 'features/modal/modalSlice'
import ConfirmModal from 'components/modals/ConfirmModal'
import OpenResponses from 'components/polls/OpenResponses'
import PredeterminedResponses from 'components/polls/PredeterminedResponses'
import SpinnerSmall from 'components/SpinnerSmall'
import { STATUS } from 'features/enums'
import { Modal } from '@mui/material'
import { useTranslation } from 'react-i18next'

export default function SinglePollPage() {
  const { t } = useTranslation('polls')
  const viewSettings = [
    { value: 'visible', title: t('management.settings.visible.title'), icon: RiBarChartHorizontalFill, summary: t('management.settings.visible.summary'), note: t('management.settings.visible.note') },
    { value: 'user', title: t('management.settings.afterVote.title'), icon: MdHowToVote, summary: t('management.settings.afterVote.summary'), note: t('management.settings.afterVote.note') },
    { value: 'hidden', title: t('management.settings.hidden.title'), icon: MdOutlineVisibilityOff, summary: t('management.settings.hidden.summary'), note: t('management.settings.hidden.note') },
  ]
  const { pollId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user, admin } = useSelector((state) => state.auth)
  const { profileChurch } = useSelector((state) => state.profileChurch)
  const {
    polls,
    responseData,
    viewResponsesStatus,
    updateViewSettingsStatus,
    isPollDeleteSuccess,
    isPollDeleteLoading,
  } = useSelector((state) => state.poll)
  const { viewConfirmModal, confirmModalData } = useSelector((state) => state.modal)
  const [selectedSetting, setSelectedSetting] = useState('')

  const pollFromList = useMemo(
    () => polls.find((poll) => poll._id === pollId),
    [polls, pollId]
  )

  const responsePoll = responseData && responseData._id === pollId ? responseData : null
  const poll = responsePoll || pollFromList
  const pollIndex = polls.findIndex((item) => item._id === pollId)
  const churchId = profileChurch && profileChurch._id ? profileChurch._id : poll?.church?._id
  const canManagePoll = Boolean(user?.isOrganization || (churchId && admin && admin[churchId]))
  const totalResponses = poll?.responseType === 'open'
    ? poll?.responses?.length || 0
    : poll?.participants?.length || 0

  useEffect(() => {
    dispatch(viewResponses({
      pollId,
      churchId: churchId || null
    }))
  }, [dispatch, pollId, churchId])

  useEffect(() => {
    if(poll?.viewResults) {
      setSelectedSetting(poll.viewResults)
    }
  }, [poll?.viewResults])

  useEffect(() => {
    if(isPollDeleteSuccess) {
      dispatch(setConfirmModalData({}))
      dispatch(setViewConfirmModal(false))
      dispatch(resetPollDeleteStatus())
      navigate(-1)
    }
  }, [dispatch, isPollDeleteSuccess, navigate])

  const handleSaveSettings = () => {
    if(!selectedSetting || selectedSetting === poll.viewResults) {
      return
    }

    dispatch(updateViewSettings({
      churchId,
      pollId,
      viewResults: selectedSetting,
      index: pollIndex
    }))
  }

  const handleDelete = () => {
    dispatch(setConfirmModalData({
      text: t('management.deleteConfirm'),
      handler: () => dispatch(deletePoll({ churchId, pollId })),
      confirmBtnBackgroundHover: '#642419',
    }))
    dispatch(setViewConfirmModal(true))
  }

  useEffect(() => {
    if(updateViewSettingsStatus === STATUS.SUCCESS) {
      toast.success(t('management.updated'))
    } else if(updateViewSettingsStatus === STATUS.ERROR) {
      toast.error(t('management.updateError'))
    }
  }, [updateViewSettingsStatus, t])

  if(viewResponsesStatus === STATUS.LOADING && !poll) {
    return (
      <PageContainer>
        <CenteredPanel>
          <SpinnerSmall border="#727272" />
        </CenteredPanel>
      </PageContainer>
    )
  }

  if(!poll) {
    return (
      <PageContainer>
        <CenteredPanel>{t('empty.notFound')}</CenteredPanel>
      </PageContainer>
    )
  }

  if(!canManagePoll) {
    return (
      <PageContainer>
        <TopAction onClick={() => navigate(-1)}>
          <MdArrowBackIosNew /> {t('management.goBack')}
        </TopAction>
        <CenteredPanel>
          {t('management.restricted')}
        </CenteredPanel>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <TopAction onClick={() => navigate(-1)}>
        <MdArrowBackIosNew /> {t('management.goBack')}
      </TopAction>

      <PollPageGrid>
        <MainColumn>
          <PollSummary>
            <PollEyebrow>Poll</PollEyebrow>
            <PollTitle>{poll.title}</PollTitle>
            <PollMeta>
              <MetaItem>{poll.responseType === 'open' ? 'Open response' : 'Preset choices'}</MetaItem>
              <MetaItem>{totalResponses} {totalResponses === 1 ? 'response' : 'responses'}</MetaItem>
              <MetaItem>{poll.viewResults === 'visible' ? 'Results visible' : poll.viewResults === 'hidden' ? 'Results hidden' : 'Visible after vote'}</MetaItem>
            </PollMeta>
          </PollSummary>

          <Panel>
            <PanelHeader>
              <PanelTitle><FaUsersRectangle /> Responses</PanelTitle>
            </PanelHeader>

            <ResponsesBody>
              {viewResponsesStatus === STATUS.LOADING && !responsePoll ? (
                <LoadingResponses>
                  <SpinnerSmall border="#727272" />
                </LoadingResponses>
              ) : responsePoll && totalResponses > 0 ? (
                responsePoll.responseType === 'open' ? (
                  <OpenResponses pollData={responsePoll} />
                ) : (
                  <PredeterminedResponses pollData={responsePoll} />
                )
              ) : (
                <EmptyState>{t('management.noResponses')}</EmptyState>
              )}
            </ResponsesBody>
          </Panel>
        </MainColumn>

        <SideColumn>
          <Panel>
            <PanelHeader>
              <PanelTitle><FiSettings /> {t('management.viewSettings')}</PanelTitle>
            </PanelHeader>
            <SettingsIntro>
              {t('management.viewSettingsHelp')}
            </SettingsIntro>

            <SettingsList>
              {viewSettings.map((setting) => {
                const Icon = setting.icon
                const selected = selectedSetting === setting.value

                return (
                  <SettingOption
                    key={setting.value}
                    selected={selected}
                    onClick={() => setSelectedSetting(setting.value)}
                  >
                    <SettingIcon selected={selected}>
                      <Icon />
                    </SettingIcon>
                    <SettingText>
                      <SettingTitle>
                        {setting.title}
                        {poll.viewResults === setting.value && <CurrentBadge>{t('management.current')}</CurrentBadge>}
                      </SettingTitle>
                      <SettingSummary>{setting.summary}</SettingSummary>
                      <SettingNote>{setting.note}</SettingNote>
                    </SettingText>
                    {selected && <FaRegCheckCircle color="#21ad46" />}
                  </SettingOption>
                )
              })}
            </SettingsList>

            <PrimaryButton
              disabled={!selectedSetting || selectedSetting === poll.viewResults || updateViewSettingsStatus === STATUS.LOADING}
              onClick={handleSaveSettings}
            >
              {updateViewSettingsStatus === STATUS.LOADING ? <SpinnerSmall /> : t('management.saveViewSettings')}
            </PrimaryButton>
          </Panel>

          <DangerPanel>
            <PanelTitle><MdOutlineDeleteOutline /> {t('delete.title')}</PanelTitle>
            <DangerText>
              {t('management.deleteHelp')}
            </DangerText>
            <DangerButton onClick={handleDelete}>{t('delete.title')}</DangerButton>
          </DangerPanel>
        </SideColumn>
      </PollPageGrid>

      <Modal
        open={viewConfirmModal}
        onClose={() => dispatch(setViewConfirmModal(false))}
        aria-labelledby="modal-modal-title"
      >
        <ModalWrapper>
          <ConfirmModal
            text={confirmModalData.text}
            handler={confirmModalData.handler}
            confirmBtnBackgroundHover={confirmModalData.confirmBtnBackgroundHover}
            extraInfo={confirmModalData.extraInfo}
            conditionToCloseModal={isPollDeleteSuccess}
            loadingState={isPollDeleteLoading}
          />
        </ModalWrapper>
      </Modal>
    </PageContainer>
  )
}

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 70px);
  padding: 20px;
  color: var(--lightThemeFontColor);
  background: #f7f8fa;
`

const TopAction = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: var(--lightThemeFontColor);
  cursor: pointer;
  padding: 8px 0;
  margin: 0 auto 10px;
  width: min(1120px, 100%);
  font-size: 14px;
`

const PollPageGrid = styled.div`
  width: min(1120px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PollSummary = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 10px #ddd;
  padding: 24px;
`

const PollEyebrow = styled.div`
  color: var(--themeColor);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 10px;
`

const PollTitle = styled.h1`
  margin: 0;
  color: var(--lightThemeFontColor);
  font-size: 28px;
  line-height: 1.25;
  font-weight: 700;
`

const PollMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
`

const MetaItem = styled.div`
  border: 1px solid #dfdfdf;
  border-radius: 6px;
  color: #606060;
  padding: 8px 10px;
  font-size: 13px;
`

const Panel = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 10px #ddd;
  padding: 20px;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--lightThemeFontColor);
  font-size: 18px;
  font-weight: 700;
`

const ResponsesBody = styled.div`
  width: 100%;
  max-height: 58vh;
  overflow-y: auto;
`

const EmptyState = styled.div`
  border: 1px dashed #cfcfcf;
  border-radius: 8px;
  padding: 24px;
  color: #777;
  text-align: center;
`

const LoadingResponses = styled(EmptyState)`
  display: flex;
  align-items: center;
  justify-content: center;
`

const SettingsIntro = styled.div`
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
`

const SettingOption = styled.button`
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 22px;
  gap: 12px;
  text-align: left;
  width: 100%;
  border: 2px solid ${props => props.selected ? '#21ad46' : '#e3e3e3'};
  background: ${props => props.selected ? '#f4fff7' : 'white'};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    border-color: #21ad46;
  }
`

const SettingIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: ${props => props.selected ? '#21ad46' : '#f0f1f3'};
  color: ${props => props.selected ? 'white' : '#606060'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`

const SettingText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const SettingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--lightThemeFontColor);
  font-weight: 700;
`

const SettingSummary = styled.div`
  color: #5b5b5b;
  font-size: 13px;
  line-height: 1.4;
`

const SettingNote = styled.div`
  color: #818181;
  font-size: 12px;
  line-height: 1.4;
`

const CurrentBadge = styled.span`
  border-radius: 5px;
  background: #e7f7ec;
  color: #168236;
  font-size: 11px;
  padding: 4px 6px;
  font-weight: 700;
`

const PrimaryButton = styled.button`
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 6px;
  background: ${props => props.disabled ? '#bcbcbc' : 'var(--themeColor)'};
  color: white;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`

const DangerPanel = styled(Panel)`
  border: 1px solid #f1d4d0;
`

const DangerText = styled.div`
  color: #77534f;
  font-size: 14px;
  line-height: 1.5;
  margin: 12px 0 16px;
`

const DangerButton = styled.button`
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #b73525;
  color: white;
  cursor: pointer;
  font-weight: 700;
`

const CenteredPanel = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 10px #ddd;
  padding: 30px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(700px, 100%);
  margin: 50px auto;
  color: #666;
`

const ModalWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
